import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { countriesAPI } from '@/lib/api';

const ComparePage = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [compareData, setCompareData] = useState([]);
  const [loading, setLoading] = useState(false);

  useState(() => {
    const fetchCountries = async () => {
      try {
        const response = await countriesAPI.getAll();
        setCountries(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch countries for comparison:', error);
        }
      }
    };
    fetchCountries();
  }, []);

  const handleCompare = async () => {
    if (selectedCountries.length < 2) {
      alert('لطفاً حداقل 2 کشور را انتخاب کنید');
      return;
    }
    setLoading(true);
    try {
      const response = await countriesAPI.compare(selectedCountries);
      setCompareData(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to compare countries:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12" data-testid="compare-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-4" data-testid="page-title">مقایسه کشورها</h1>
          <p className="text-zinc-400">تا 3 کشور را برای مقایسه انتخاب کنید</p>
        </div>

        {compareData.length === 0 && (
          <div className="glass-effect border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-xl font-medium text-white mb-6">انتخاب کشورها</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {countries.map((country) => (
                <label
                  key={country.id}
                  className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedCountries.includes(country.id)
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedCountries.includes(country.id)}
                    onChange={(e) => {
                      if (e.target.checked && selectedCountries.length < 3) {
                        setSelectedCountries([...selectedCountries, country.id]);
                      } else if (!e.target.checked) {
                        setSelectedCountries(selectedCountries.filter((id) => id !== country.id));
                      }
                    }}
                    disabled={!selectedCountries.includes(country.id) && selectedCountries.length >= 3}
                    className="w-4 h-4"
                  />
                  <span className="text-2xl">{country.flag_emoji}</span>
                  <span className="text-sm text-white">{country.name_fa}</span>
                </label>
              ))}
            </div>
            <button
              onClick={handleCompare}
              disabled={selectedCountries.length < 2 || loading}
              data-testid="compare-button"
              className="w-full px-6 py-4 bg-[#D4AF37] text-black rounded-full font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            >
              {loading ? 'در حال مقایسه...' : 'مقایسه کنید'}
            </button>
          </div>
        )}

        {compareData.length > 0 && (
          <>
            <button
              onClick={() => { setCompareData([]); setSelectedCountries([]); }}
              className="mb-6 px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors"
            >
              مقایسه جدید
            </button>

            <div className="glass-effect border border-white/10 rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-right text-zinc-400 font-medium">مورد مقایسه</th>
                    {compareData.map((country) => (
                      <th key={country.id} className="p-4 text-center">
                        <div className="text-3xl mb-2">{country.flag_emoji}</div>
                        <div className="text-white font-medium">{country.name_fa}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="p-4 text-zinc-400">کیفیت زندگی</td>
                    {compareData.map((country) => (
                      <td key={country.id} className="p-4 text-center text-white">{country.quality_of_life_score}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="p-4 text-zinc-400">امنیت</td>
                    {compareData.map((country) => (
                      <td key={country.id} className="p-4 text-center text-white">{country.security_score}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="p-4 text-zinc-400">هزینه زندگی</td>
                    {compareData.map((country) => (
                      <td key={country.id} className="p-4 text-center text-white">{country.avg_living_cost}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="p-4 text-zinc-400">متوسط حقوق</td>
                    {compareData.map((country) => (
                      <td key={country.id} className="p-4 text-center text-white">{country.avg_salary}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="p-4 text-zinc-400">آموزش</td>
                    {compareData.map((country) => (
                      <td key={country.id} className="p-4 text-center text-white">{country.education_score}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-4 text-zinc-400">بهداشت</td>
                    {compareData.map((country) => (
                      <td key={country.id} className="p-4 text-center text-white">{country.healthcare_score}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ComparePage;